import Spinner from "@/components/Ui/Tools/Spinner/Spinner";
import { baseURL } from "@/services/api";
import { ICampaignDetail, IRelateds } from "global-types/global.types";
import { GetStaticPaths, GetStaticProps } from "next";
import styled from "styled-components";
import "bootstrap/dist/css/bootstrap.min.css";
import Link from "next/link";
import { t, localePrefix } from "@/helper/helper";
import Image from "next/image";
import axios from "axios";
import { SITE_NAME, SITE_URL } from "@/helper/constant";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Row } from "react-bootstrap";
import { useCount } from "@/global-state/global.state";
import useCampaignDetail from "@/hooks/pages/campaigns/useCampaignDetail";
import useInterSectionObserver from "@/hooks/tools/useInterSectionObserver";
import CampaignsCardDetail from "@/components/Ui/Pages/CampaignsCardDetail/CampaignsCardDetail";

interface Props {
  data: ICampaignDetail;
  locale: string;
}

export default function CampaignsDetail({ locale, data }: Props) {
  const { asPath, query } = useRouter();
  const campaign = data.data.campaign;

  const [paginateData, setPaginateData] = useState<IRelateds[]>(
    data.data.products.data,
  );
  const [view, setView] = useState<boolean>(false);
  const { count, setCount, destroyCount } = useCount();

  const { data: campaignData, isFetching } = useCampaignDetail({
    id: query.id as string,
    initialData: data,
    setPaginationData: setPaginateData,
    params: {
      page: count,
    },
  });

  useInterSectionObserver({
    elementClassName: ".product-card",
    render: paginateData,
    setView,
  });

  useEffect(() => {
    if (campaignData && campaignData.data.products.data.length !== 0) {
      if (
        count <= campaignData.data.products.pagination.last_page &&
        !isFetching
      ) {
        setCount(1);
      }
    }
  }, [view]);

  useEffect(() => {
    return () => {
      destroyCount();
    };
  }, []);

  return (
    <>
      <Wrapper>
        <NextSeo
          title={campaign.title + " | " + SITE_NAME}
          description={campaign.description.substring(0, 160) || ""}
          canonical={SITE_URL + localePrefix(locale) + asPath}
          additionalMetaTags={[
            {
              name: "title",
              content: campaign.title + " | " + SITE_NAME,
            },
          ]}
          openGraph={{
            url: SITE_URL + localePrefix(locale) + asPath,
            title: campaign.title + " " + SITE_NAME,
            description: campaign.description.substring(0, 300),
            images: [
              {
                url: campaign.cover_image || campaign.header_image || "",
              },
            ],
            siteName: SITE_NAME,
          }}
        />

        <div className="campaign">
          <div className="breadcrumb">
            <Link href={`${localePrefix(locale) || "/"}`}>{t("homePage", locale)}</Link>
            <span className="breadcrumb-seperator" />
            <Link href={`${localePrefix(locale)}/campaigns`}>{t("campaigns", locale)}</Link>
            <span className="breadcrumb-seperator" />
            <Link href={`${localePrefix(locale)}/campaigns/${campaign.id}`}>
              {campaign.title}
            </Link>
          </div>

          {(campaign.header_image || campaign.cover_image) && (
            <div className="campaign-header">
              <Image
                width={1128}
                height={300}
                src={campaign.header_image || campaign.cover_image || ""}
                alt={campaign.title}
                style={{ objectFit: "cover", borderRadius: "6px" }}
              />
            </div>
          )}

          <div className="campaign-content">
            <div className="campaign-about">
              <h1>{campaign.title}</h1>
              <div className="campaign-dates">
                <span className="date-label">{t("startDate", locale)}: </span>
                <span className="date-value">{campaign.start_date}</span>
                <span className="date-separator"> - </span>
                <span className="date-label">{t("endDate", locale)}: </span>
                <span className="date-value">{campaign.end_date}</span>
              </div>
              {campaign.description && (
                <div className="campaign-description">
                  {campaign.description}
                </div>
              )}
              {campaign.content && (
                <div
                  className="campaign-content-html"
                  dangerouslySetInnerHTML={{ __html: campaign.content }}
                />
              )}
            </div>

            {paginateData && paginateData.length > 0 && (
              <div className="campaign-products">
                <div className="campaign-products-header">
                  <h2>{t("campaignProducts", locale)}</h2>
                </div>

                <Row>
                  {paginateData.map((item, index) => (
                    <CampaignsCardDetail
                      locale={locale}
                      data={item}
                      md="3"
                      slug={`${localePrefix(locale)}/` + item.slug}
                      key={index.toString() + item.title}
                    />
                  ))}
                </Row>

                {count > 1 && isFetching && (
                  <div className="spinner-pagination">
                    <Spinner />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Wrapper>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const campaignsData = await fetch(baseURL + "/campaigns");
    const campaignsDataJson = await campaignsData.json();

    const locales = ["en", "ru", "az"];

    const paths = campaignsDataJson.data.slice(0, 10).flatMap((campaign: any) =>
      locales.map((locale) => ({
        params: { id: campaign.id.toString() },
        locale: locale,
      })),
    );

    return {
      paths,
      fallback: "blocking",
    };
  } catch (error) {
    return {
      paths: [],
      fallback: "blocking",
    };
  }
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale } = ctx;
  const { id } = ctx.params!;

  try {
    const campaignDetailData = await axios.get(baseURL + `/campaigns/${id}`, {
      headers: {
        "x-localization": locale || "az",
      },
    });

    return {
      props: {
        data: campaignDetailData.data,
        locale,
        id,
      },
      revalidate: 60,
    };
  } catch (error) {
    return {
      notFound: true,
    };
  }
};

const Wrapper = styled.div`
  .spinner-pagination {
    display: flex;
    width: 100%;
    justify-content: center;
    align-items: center;
    padding: 2rem 0;
    margin-top: 2rem;

    img {
      width: 50px;
      height: 50px;
    }
  }

  .campaign {
    width: 1128px;
    margin: 0 auto;
    padding-bottom: 7rem;

    @media screen and (max-width: 1199px) {
      width: 100%;
      padding: 0 16px;
    }

    .campaign-header {
      margin: 2.4rem 0;
      width: 100%;
      height: 300px;
      border-radius: 6px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .campaign-content {
      margin-top: 5.6rem;

      .campaign-about {
        width: 812px;
        margin: auto;
        margin-bottom: 4.8rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;

        h1 {
          font-style: normal;
          font-weight: bold;
          font-size: 36px;
          line-height: 150%;
          color: #040941;
          margin-bottom: 2.4rem;
        }

        .campaign-dates {
          margin-bottom: 2.4rem;
          font-size: 1.6rem;
          color: #989eaf;

          .date-label {
            font-weight: 500;
          }

          .date-value {
            font-weight: 600;
            color: #040941;
          }

          .date-separator {
            margin: 0 1rem;
            color: #040941;
          }
        }

        .campaign-description {
          font-style: normal;
          font-weight: 400;
          font-size: 2rem;
          line-height: 30px;
          color: #989eaf;
          text-align: center;
          margin-bottom: 30px;
        }

        .campaign-content-html {
          font-style: normal;
          font-weight: 400;
          font-size: 1.8rem;
          line-height: 28px;
          color: #989eaf;
          text-align: left;
          width: 100%;

          p {
            margin-bottom: 1.6rem;
          }

          h2,
          h3,
          h4,
          h5,
          h6 {
            color: #040941;
            margin: 2.4rem 0 1.6rem 0;
          }
        }
      }
    }
  }

  @media screen and (max-width: 992.9px) {
    .campaign .campaign-content .campaign-about {
      width: 100%;
    }
  }

  @media screen and (max-width: 767.9px) {
    .campaign {
      .campaign-header {
        height: 200px;
        margin: 1.6rem 0;
      }

      .campaign-content {
        margin-top: 24px;

        .campaign-about {
          width: 100%;
          margin-bottom: 24px;

          h1 {
            font-size: 2.8rem;
            margin-bottom: 2.4rem;
          }

          .campaign-dates {
            font-size: 1.4rem;
            margin-bottom: 1.6rem;
          }

          .campaign-description {
            font-size: 1.6rem;
            font-weight: 500;
            line-height: 150%;
          }

          .campaign-content-html {
            font-size: 1.6rem;
            line-height: 24px;
          }
        }
      }
    }

    .campaign-products {
      margin-top: 4.8rem;

      .campaign-products-header {
        margin-bottom: 3.2rem;
        text-align: center;

        h2 {
          font-style: normal;
          font-weight: bold;
          font-size: 2.8rem;
          line-height: 150%;
          color: #040941;
          margin-bottom: 1.2rem;
        }

        p {
          font-style: normal;
          font-weight: 400;
          font-size: 1.6rem;
          color: #989eaf;
        }
      }
    }
  }

  @media screen and (max-width: 992.9px) {
    .campaign .campaign-content .campaign-about {
      width: 100%;
    }
  }

  @media screen and (max-width: 767.9px) {
    .campaign {
      .campaign-header {
        height: 200px;
        margin: 1.6rem 0;
      }

      .campaign-content {
        margin-top: 24px;

        .campaign-about {
          width: 100%;
          margin-bottom: 24px;

          h1 {
            font-size: 2.8rem;
            margin-bottom: 2.4rem;
          }

          .campaign-dates {
            font-size: 1.4rem;
            margin-bottom: 1.6rem;
          }

          .campaign-description {
            font-size: 1.6rem;
            font-weight: 500;
            line-height: 150%;
          }

          .campaign-content-html {
            font-size: 1.6rem;
            line-height: 24px;
          }
        }

        .campaign-products {
          margin-top: 2.4rem;

          .campaign-products-header {
            margin-bottom: 2.4rem;

            h2 {
              font-size: 2.4rem;
            }

            p {
              font-size: 1.4rem;
            }
          }
        }
      }
    }
  }
`;
